import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import DataTable from 'react-data-table-component';
import { get, del, put } from '../../controllers/apiController';
import { useAuth } from '../../context/AuthContext';
import { Select2WColor } from '../../components/Select2';
import { formatPhoneTR } from '../../utils/phone';
import { parseCommaList } from '../../utils/list';
import { getProject, getProjectLabel, withProject } from '../../utils/project';

import Swal from 'sweetalert2'
import { toast } from 'react-toastify';

const parseEtiketIds = (etiketlerStr) => {
    if (!etiketlerStr) return [];
    return etiketlerStr.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
};

const AcenteYonetimi = () => {
    const { user } = useAuth();
    const canManage = user.Kullanici_Yetki == 0 || user.Kullanici_Yetki == 1;

    const [isLoading, setIsLoading] = useState(true);

    const [acenteler, setAcenteler] = useState([]);
    const [temsilciler, setTemsilciler] = useState([]);

    const [etiketlerRaw, setEtiketlerRaw] = useState([]);
    const [etiketOptions, setEtiketOptions] = useState([]);
    const [selectedAcenteForLabels, setSelectedAcenteForLabels] = useState(null);
    const [newLabels, setNewLabels] = useState(null);
    const [showLabelsModal, setShowLabelsModal] = useState(false);

    const [search, setSearch] = useState("");
    const [selectedLabels, setSelectedLabels] = useState([]);

    useEffect(() => {
        document.title = `Acente Yönetimi ${getProjectLabel()} | LEVELUP CRM`;

        const fetchAcenteler = async () => {
            try {
                const res = await get("acenteler", { project: getProject() });

                const acentelerMapped = res.map(acente => {
                    return {
                        id: acente.Acente_ID,
                        ad: acente.Acente_Adi,
                        temsilciAdi: acente.Temsilci_Adi,
                        temsilciId: acente.Temsilci_ID,
                        uyruk: acente.Uyruk,
                        telefon: acente.Telefon,
                        tarih: acente.Son_Arama_Tarihi,
                        aramaSayisi: acente.Arama_Sayisi || 0,
                        etiketIds: parseEtiketIds(acente.Etiketler)
                    }
                });

                const visibleAcenteler = canManage
                    ? acentelerMapped
                    : acentelerMapped.filter(acente => acente.temsilciId === user.Kullanici_ID);

                visibleAcenteler.sort((a, b) => {
                    const aTime = a.tarih ? new Date(a.tarih).getTime() : 0;
                    const bTime = b.tarih ? new Date(b.tarih).getTime() : 0;

                    if (aTime !== bTime) return bTime - aTime;

                    return (a.ad || '').localeCompare(b.ad || '', 'tr');
                });

                setAcenteler(visibleAcenteler);

                setIsLoading(false);
            } catch (err) {
                toast.error("Veriler alınırken bir hata oluştu!");
            }
        }

        const fetchEtiketler = async () => {
            try {
                const res = await get("labels/");

                setEtiketlerRaw(res);
                setEtiketOptions(res.map(etiket => ({
                    value: etiket.Etiket_ID,
                    label: etiket.Etiket_Adi,
                    color: "#" + etiket.Etiket_Renk
                })));
            } catch (err) {
                toast.error("Etiket verileri alınırken bir hata oluştu!");
            }
        }

        const fetchTemsilciler = async () => {
            try {
                const res = await get("users/company-users");

                setTemsilciler(res.map(temsilci => ({
                    value: temsilci.Kullanici_ID,
                    label: `${temsilci.Kullanici_Adi} ${temsilci.Kullanici_Soyadi}`
                })));
            } catch (err) {
                toast.error("Temsilci verileri alınırken bir hata oluştu!");
            }
        }

        fetchAcenteler();
        fetchEtiketler();
        fetchTemsilciler();
    }, []);

    const filteredAcenteler = useMemo(() => {
        let filtered = acenteler;

        if (selectedLabels.length > 0) {
            filtered = filtered.filter(acente =>
                (acente.etiketIds || []).some(id => selectedLabels.includes(id))
            );
        }

        if (search) {
            const term = search.toLowerCase();

            filtered = filtered.filter(acente => {
                const danismanAdi = temsilciler.find(t => t.value === acente.temsilciId)?.label || '';

                return (
                    String(acente.ad ?? '').toLowerCase().includes(term) ||
                    String(acente.temsilciAdi ?? '').toLowerCase().includes(term) ||
                    String(acente.telefon ?? '').toLowerCase().includes(term) ||
                    String(danismanAdi ?? '').toLowerCase().includes(term)
                );
            });
        }

        return filtered;
    }, [acenteler, search, selectedLabels, temsilciler]);

    const handleSearch = (event) => {
        setSearch(event.target.value);
    };

    const filterHandler = (e) => {
        const input = e.currentTarget.querySelector('input');
        const value = parseInt(input.value);

        input.checked = !input.checked;

        setSelectedLabels((prevLabels) => {
            if (input.checked) {
                return prevLabels.includes(value) ? prevLabels : [...prevLabels, value];
            } else {
                return prevLabels.filter(label => label !== value);
            }
        });
    };

    const editAcenteLabels = (acente) => {
        setSelectedAcenteForLabels(acente);
        setNewLabels(etiketOptions.filter(etiket => acente.etiketIds.includes(etiket.value)));
        setShowLabelsModal(true);
    }

    const handleLabelsEditSubmit = async () => {
        try {
            const labelIds = (newLabels || []).map(label => label.value);

            await put("acenteler/change-labels", {
                project: getProject(),
                agencyId: selectedAcenteForLabels.id,
                labelIds: labelIds
            });

            setAcenteler((acenteler) => acenteler.map(acente =>
                acente.id === selectedAcenteForLabels?.id
                    ? { ...acente, etiketIds: labelIds }
                    : acente
            ));

            toast.success("Etiket başarıyla değiştirildi.");

            setShowLabelsModal(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Etiket değiştirilirken bir hata oluştu!");
        }
    }

    const paginationComponentOptions = {
        rowsPerPageText: 'Sayfa başına satır:',
        rangeSeparatorText: 'toplam',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Tümü',
        firstPageText: 'İlk',
        previousPageText: 'Önceki',
        nextPageText: 'Sonraki',
        lastPageText: 'Son',
        noRowsPerPage: false,
    };

    const customStyles = {
        noData: {
            style: {
                padding: '24px',
                textAlign: 'center',
            },
        },
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Acente Silinsin mi?',
            icon: 'error',
            showCancelButton: true,
            confirmButtonText: 'Sil',
            cancelButtonText: 'İptal'
        }).then(async (result) => {
            if (result.isConfirmed) {
                await del(`acenteler/${id}`);

                setAcenteler(acenteler.filter(acente => acente.id !== id));

                toast.success('Acente başarıyla silindi');
            }
        })

    }

    const columns = [
        {
            id: 'ad',
            name: 'Acente Adı',
            sortable: true,
            selector: row => row.ad,
            cell: row => (
                <a href={withProject(`./agency-detail?id=${row.id}`)}>{row.ad}</a>
            )
        },
        {
            name: 'Temsilci Adı',
            sortable: true,
            selector: row => row.temsilciAdi || '-',
            cell: row => parseCommaList(row.temsilciAdi).join(', ') || '-'
        },
        {
            name: 'Uyruk',
            sortable: true,
            selector: row => row.uyruk || '-'
        },
        {
            name: 'Telefon',
            sortable: true,
            selector: row => row.telefon || '-',
            cell: row => {
                if (!row.telefon) return '-';

                const phone = formatPhoneTR(row.telefon);

                return <a href={`tel:${phone.tel}`}>{phone.display}</a>;
            }
        },
        {
            id: 'tarih',
            name: 'Son Arama Tarihi',
            sortable: true,
            selector: row => row.tarih || '-',
            cell: row => {
                if (!row.tarih) return '-';

                const tarih = new Date(row.tarih);

                if (isNaN(tarih.getTime())) return '-';

                return <span>{`${tarih.getDate().toString().padStart(2, '0')}.${(tarih.getMonth() + 1).toString().padStart(2, '0')}.${tarih.getFullYear()} ${tarih.getHours().toString().padStart(2, '0')}:${tarih.getMinutes().toString().padStart(2, '0')}`}</span>;
            }
        },
        {
            id: 'aramaSayisi',
            name: 'Arama Sayısı',
            sortable: true,
            width: '130px',
            selector: row => row.aramaSayisi,
            cell: row => (
                <span className="badge bg-primary fs-12" style={{ fontWeight: 700 }}>{row.aramaSayisi}</span>
            )
        },
        {
            name: 'Danışman',
            sortable: true,
            selector: row => temsilciler.find(t => t.value === row.temsilciId)?.label || '-'
        },
        {
            name: 'Etiket',
            width: '200px',
            cell: row => {
                const etiketler = etiketlerRaw.filter(etiket => row.etiketIds.includes(etiket.Etiket_ID));

                return (
                    <div className="d-flex gap-2 align-items-center">
                        {etiketler.length > 0 ? (
                            <>
                                <div className="badge" style={{ backgroundColor: `#${etiketler[0].Etiket_Renk}`, color: `#${etiketler[0].Etiket_Yazi_Renk}` }}>
                                    {etiketler[0].Etiket_Adi}
                                </div>
                                {etiketler.length > 1 && (
                                    <div className="badge" style={{ backgroundColor: "#efefef", color: "#7f7f7f" }}>
                                        +{etiketler.length - 1}
                                    </div>
                                )}
                            </>
                        ) : (
                            <span className="text-muted">-</span>
                        )}
                        {canManage && (
                            <button className="text-primary p-0 bg-transparent border-0" onClick={() => editAcenteLabels(row)}>
                                <i className="feather-edit"></i>
                            </button>
                        )}
                    </div>
                );
            }
        },
        ...(canManage
            ? [
                {
                    name: 'Aksiyon',
                    width: '90px',
                    cell: row => (
                        <div className="d-flex gap-2">
                            <a href="#!" className="avatar-text avatar-md" onClick={() => handleDelete(row.id)}>
                                <i className="feather feather-trash fs-6"></i>
                            </a>
                        </div>
                    )
                }
            ]
            : []
        )
    ];

    const modalBackdropStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1040
    };

    return (
        <Layout>
            <main className="nxl-container">
                <div className="nxl-content">
                    <div className="page-header">
                        <div className="page-header-left d-flex align-items-center">
                            <div className="page-header-title">
                                <h5 className="m-b-10">LEVELUP CRM</h5>
                            </div>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item"><a href="./">Anasayfa</a></li>
                                <li className="breadcrumb-item">Acente Yönetimi {getProjectLabel()}</li>
                            </ul>
                        </div>
                    </div>
                    <div className="main-content">
                        <div className="row">
                            <div className="col-xxl-12 col-md-12">
                                <div className="card stretch stretch-full">
                                    <div className="card-header flex-wrap gap-2">
                                        <h5 className="card-title">Tüm Acenteler</h5>
                                        <div className="card-header-action d-flex align-items-center gap-2 flex-nowrap">
                                            <input
                                                type="text"
                                                placeholder="Acente, temsilci, telefon veya danışman ara..."
                                                className="form-control"
                                                style={{ width: '260px' }}
                                                value={search}
                                                onChange={handleSearch}
                                            />
                                            <div className="dropdown">
                                                <a className="btn btn-icon btn-light-brand" data-bs-toggle="dropdown" data-bs-offset="0, 10" data-bs-auto-close="outside" title="Etiket Filtrele">
                                                    <i className="feather-filter"></i>
                                                    {selectedLabels.length > 0 && (
                                                        <span className="badge bg-primary ms-1">{selectedLabels.length}</span>
                                                    )}
                                                </a>
                                                <div className="dropdown-menu dropdown-menu-end">
                                                    {etiketOptions.length > 0 ? etiketOptions.map(etiket => (
                                                        <div className="dropdown-item" key={etiket.value} onClick={filterHandler}>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="checkbox" id={`etiket-filter-${etiket.value}`} value={etiket.value} hidden />
                                                                <label className="form-check-label w-100" htmlFor={`etiket-filter-${etiket.value}`}>
                                                                    <span className="wd-7 ht-7 rounded-circle d-inline-block me-3" style={{ backgroundColor: etiket.color }}></span>
                                                                    <span>{etiket.label}</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    )) : (
                                                        <span className="dropdown-item-text text-muted">Etiket bulunamadı</span>
                                                    )}
                                                    <style>
                                                        {`
                                                            .dropdown-item, .form-check-label {
                                                                cursor: pointer;
                                                            }
                                                            .dropdown-item:has(> .form-check input:checked) {
                                                                background-color: #e8f0fe;
                                                            }
                                                            .form-check {
                                                                cursor: pointer;
                                                                padding-left: 0 !important;
                                                            }
                                                        `}
                                                    </style>
                                                </div>
                                            </div>
                                            {canManage && (
                                                <a href={withProject("./create-agency")} className="btn btn-light">
                                                    <i className="feather-plus"></i>
                                                    Yeni Ekle
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="card-body custom-card-action p-2">
                                        {isLoading ? (
                                            <div className="w-100 d-flex align-items-center justify-content-center spinner-wrapper">
                                                <div class="spinner-border text-primary" role="status">
                                                    <span class="visually-hidden">Yükleniyor...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="table-responsive mb-3">
                                                <DataTable
                                                    columns={columns}
                                                    data={filteredAcenteler}
                                                    className="table table-hover mb-0"
                                                    pagination
                                                    responsive
                                                    defaultSortFieldId="tarih"
                                                    defaultSortAsc={false}
                                                    noDataComponent="Görüntülenecek kayıt bulunamadı"
                                                    paginationComponentOptions={paginationComponentOptions}
                                                    customStyles={customStyles}
                                                    paginationPerPage={10}
                                                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {canManage && showLabelsModal && selectedAcenteForLabels && (
                <div className="modal fade show" style={{ display: 'block' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Etiketleri Düzenle</h5>
                                <button type="button" className="btn-close" onClick={() => setShowLabelsModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Etiketler</label>
                                    <Select2WColor
                                        key="agencyLabelSelect"
                                        onChange={(val) => setNewLabels(val)}
                                        value={newLabels}
                                        isSearchable
                                        isClearable
                                        options={etiketOptions}
                                        isMulti
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowLabelsModal(false)}>İptal</button>
                                <button type="submit" className="btn btn-primary" onClick={handleLabelsEditSubmit}>Kaydet</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {canManage && showLabelsModal && (
                <div style={modalBackdropStyle} onClick={() => setShowLabelsModal(false)} />
            )}
        </Layout>
    );
};

export default AcenteYonetimi;
